/**
 * HopPathVisualization — Horizontal stepper rendering per-hop intelligence
 * for backward/forward trace paths in AutoInvestigateCard.
 *
 * Compact mode (default): fits inside 520px card accordion.
 * Falls back to plain addr → addr when hop_details is empty.
 */

import React from 'react';
import clsx from 'clsx';

// ── Types ──────────────────────────────────────────────────────────

export interface HopDetail {
    address: string;
    entity_name?: string | null;
    entity_type?: string | null;
    dwell_time_seconds?: number | null;
    value_change_pct?: number | null;
    classification: string;
    incoming_tx?: { hash?: string; timestamp?: number; value?: number };
    outgoing_tx?: { hash?: string; timestamp?: number; value?: number };
    fan_in?: number;
    fan_out?: number;
    fund_through_rate?: number;
}

export interface TraceEdge {
    tx_hash?: string;
    value?: number;
    timestamp?: number;
    token_symbol?: string;
}

export interface HopPathVisualizationProps {
    path: string[];
    hopDetails: HopDetail[];
    edges?: TraceEdge[];
    targetEntity?: { name?: string; type?: string; address?: string };
    sourceEntity?: { entity_name?: string; entity_type?: string; address?: string };
    compact?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────

const CLASSIFICATION_CONFIG: Record<string, { icon: string; color: string; label: string }> = {
    relay:         { icon: '⚡', color: 'text-amber-500',   label: 'Relay' },
    service:       { icon: '🏦', color: 'text-emerald-500', label: 'Service' },
    storage:       { icon: '💾', color: 'text-blue-500',    label: 'Storage' },
    intermediary:  { icon: '🔄', color: 'text-muted-foreground',   label: 'Intermediary' },
    collector:     { icon: '📥', color: 'text-primary',  label: 'Collector' },
    distributor:   { icon: '📤', color: 'text-orange-500',  label: 'Distributor' },
};

function getClassConfig(classification: string) {
    return CLASSIFICATION_CONFIG[classification] || CLASSIFICATION_CONFIG.intermediary;
}

function formatDwell(seconds: number | null | undefined): string | null {
    if (seconds == null) return null;
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
    return `${(seconds / 86400).toFixed(1)}d`;
}

function formatValue(value: number | null | undefined): string | null {
    if (value == null || value === 0) return null;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
}

function shortAddr(addr: string): string {
    if (!addr || addr.length < 12) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

// ── Component ──────────────────────────────────────────────────────

export const HopPathVisualization: React.FC<HopPathVisualizationProps> = ({
    path,
    hopDetails,
    edges = [],
    targetEntity,
    sourceEntity,
    compact = true,
}) => {
    if (!path || path.length === 0) return null;

    // Build a lookup: address → HopDetail for intermediate nodes
    const hopMap = new Map<string, HopDetail>();
    for (const hd of hopDetails) {
        hopMap.set(hd.address, hd);
    }

    // If no hop_details at all, fall back to plain rendering
    const hasHopIntel = hopDetails.length > 0;

    // Source / destination labels
    const sourceLabel = sourceEntity?.entity_name
        || (sourceEntity?.address ? shortAddr(sourceEntity.address) : null);
    const sourceType = sourceEntity?.entity_type;

    const destLabel = targetEntity?.name
        || (targetEntity?.address ? shortAddr(targetEntity.address) : null);
    const destType = targetEntity?.type;

    if (!hasHopIntel) {
        // Fallback: plain addr → addr rendering (backward compat)
        return (
            <div className="text-sm p-1.5 bg-secondary/50 rounded border border-border">
                {(sourceLabel || destLabel) && (
                    <span className="text-xs text-primary font-medium inline-block mb-0.5">
                        {sourceLabel || destLabel}
                        {(sourceType || destType) && ` (${sourceType || destType})`}
                    </span>
                )}
                <div className="flex items-center gap-1.5 flex-wrap">
                    {path.map((addr, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span className="text-muted-foreground">&#8594;</span>}
                            <span className="font-mono text-xs" title={addr}>
                                {shortAddr(addr)}
                            </span>
                        </React.Fragment>
                    ))}
                </div>
                {edges.length > 0 && (
                    <span className="text-[10px] text-muted-foreground mt-0.5 inline-block">
                        {edges.length} edges
                    </span>
                )}
            </div>
        );
    }

    // ── Rich stepper rendering ─────────────────────────────────────

    return (
        <div className="p-1.5 bg-secondary/50 rounded border border-border space-y-1">
            {/* Entity header */}
            {(sourceLabel || destLabel) && (
                <div className="text-xs text-primary font-medium">
                    {sourceLabel || destLabel}
                    {(sourceType || destType) && (
                        <span className="text-muted-foreground ml-1">({sourceType || destType})</span>
                    )}
                </div>
            )}

            {/* Stepper */}
            <div className="flex items-center gap-0 flex-wrap">
                {path.map((addr, i) => {
                    const isFirst = i === 0;
                    const isLast = i === path.length - 1;
                    const hop = hopMap.get(addr);
                    const edgeBefore = i > 0 ? edges[i - 1] : undefined;

                    // Edge value label (shown before this node, except first)
                    const edgeValue = edgeBefore ? formatValue(edgeBefore.value) : null;

                    // Node classification (only intermediate nodes have hop_details)
                    const config = hop ? getClassConfig(hop.classification) : null;
                    const dwell = hop ? formatDwell(hop.dwell_time_seconds) : null;

                    // Entity label for first/last nodes
                    let nodeLabel: string;
                    if (isFirst && sourceLabel) {
                        nodeLabel = sourceLabel;
                    } else if (isLast && destLabel) {
                        nodeLabel = destLabel;
                    } else if (hop?.entity_name) {
                        nodeLabel = hop.entity_name;
                    } else {
                        nodeLabel = shortAddr(addr);
                    }

                    return (
                        <React.Fragment key={i}>
                            {/* Edge connector with value */}
                            {!isFirst && (
                                <div className="flex items-center mx-0.5 shrink-0">
                                    {edgeValue && (
                                        <span className="text-[10px] text-muted-foreground mr-0.5">
                                            {edgeValue}
                                        </span>
                                    )}
                                    <span className="text-muted-foreground text-xs">→</span>
                                </div>
                            )}

                            {/* Node */}
                            <div
                                className={clsx(
                                    'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] shrink-0',
                                    isFirst || isLast
                                        ? 'bg-primary/10 border border-primary/30 text-primary font-medium'
                                        : 'bg-muted/50 border border-border text-foreground'
                                )}
                                title={addr}
                            >
                                {/* Classification badge for intermediate hops */}
                                {config && (
                                    <span className={clsx(config.color, 'shrink-0')} title={config.label}>
                                        {config.icon}
                                    </span>
                                )}

                                {/* Endpoint icons */}
                                {isFirst && !config && (
                                    <span className="shrink-0">📍</span>
                                )}
                                {isLast && !config && destType && (
                                    <span className="shrink-0">
                                        {destType === 'Exchange' ? '🏦' : '🎯'}
                                    </span>
                                )}

                                {/* Label */}
                                <span className="font-mono truncate max-w-[80px]">
                                    {nodeLabel}
                                </span>

                                {/* Dwell time badge */}
                                {dwell && (
                                    <span className="text-[8px] text-muted-foreground bg-muted px-0.5 rounded shrink-0">
                                        {dwell}
                                    </span>
                                )}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Compact metadata footer */}
            {compact && (
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span>{path.length - 1} hops</span>
                    {hopDetails.some(h => h.classification === 'relay') && (
                        <span className="text-amber-500">⚡ relay detected</span>
                    )}
                    {hopDetails.some(h => h.classification === 'storage') && (
                        <span className="text-blue-500">💾 storage detected</span>
                    )}
                    {hopDetails.some(h => h.classification === 'collector') && (
                        <span className="text-primary">📥 collector</span>
                    )}
                    {hopDetails.some(h => h.classification === 'distributor') && (
                        <span className="text-orange-500">📤 distributor</span>
                    )}
                </div>
            )}
        </div>
    );
};

export default HopPathVisualization;
